/**
 * Material Icon component wrapper for Material Symbols Rounded font
 * Uses the Material Symbols web font for lightweight, scalable icons
 */

// The icon name union is platform-neutral (derived from @material-symbols/metadata) and lives in
// @elara/client-core so data models can type icon fields without importing this DOM component
// (ELARA-700). Imported for local use in MaterialIconProps and re-exported so existing
// #ui/MaterialIcon consumers are unchanged.
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';
import type { IconSize } from '@elara/client-core/ui-contracts/icon-size';

import React from 'react';

import { cn } from '@elara/client-core/lib/utils';
import { IconSizes } from '@elara/client-core/ui-contracts/icon-size';

export type { ExtraIconName, IconName } from '@elara/client-core/ui-contracts/icon-name';
// Re-exported so existing `#ui/MaterialIcon` consumers keep importing the size scale from here,
// even though it's now defined platform-neutrally in `@elara/client-core` (shared with mobile).
export type { IconSize } from '@elara/client-core/ui-contracts/icon-size';
export { IconSizes } from '@elara/client-core/ui-contracts/icon-size';

export interface MaterialIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** The icon name from Material Symbols */
  icon: IconName;
  /** Fill state (0 = outlined, 1 = filled) */
  filled?: boolean;
  /** Weight using Tailwind font weight labels */
  weight?: keyof typeof weightMap;
  /** Optical size (20-48) */
  size?: IconSize;
  /** Grade (-50 to 200) for fine-tuning */
  grade?: number;
}

/**
 * Material Icon component using Material Symbols Rounded font.
 *
 * @example
 * ```tsx
 * <MaterialIcon icon="home" />
 * <MaterialIcon icon="favorite" filled />
 * <MaterialIcon icon="settings" weight="semibold" />
 * ```
 */
// Map Tailwind font weight labels to Material Symbols numeric values
const weightMap = {
  thin: 100,
  extralight: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
} as const;

export const MaterialIcon = React.forwardRef<HTMLSpanElement, MaterialIconProps>(
  ({ icon, filled = false, weight = 'light', size = IconSizes.sm, grade = 0, className, style, ...props }, ref) => {
    const iconSize = typeof size === 'number' ? size : IconSizes[size];
    const isAutoSize = iconSize === 0;
    const sizeValue = isAutoSize ? '1em' : `${iconSize}px`;
    return (
      <span
        ref={ref}
        className={cn('material-symbols-rounded', className)}
        style={{
          fontVariationSettings: `'FILL' ${filled ? 1 : 0}, 'wght' ${weightMap[weight]}, 'GRAD' ${grade}, 'opsz' ${iconSize}`,
          transition: 'font-variation-settings 250ms ease-out',
          fontSize: sizeValue,
          width: sizeValue,
          height: sizeValue,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          ...style,
        }}
        {...props}
      >
        {icon}
      </span>
    );
  },
);

MaterialIcon.displayName = 'MaterialIcon';
